import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LocaleDirection, LocaleType } from '@core/enums';
import { filter, Subscription, takeUntil } from 'rxjs';
import { PlaceAdService } from '../core/services/place-ad.service';

import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { LocaleService } from '../shared/services/locale.service';
import { SnackService } from '../shared/services/snack.service';
import { ViewportService } from '../shared/services/viewport.service';
import { User } from '@sentry/angular-ivy';
import { DestroyService } from '@core/services/destroy.service';
import { AdsService } from '@shared/services/ads.service';
import { ApiService } from '@shared/services/api.service';
import { AuthService } from '@shared/services/auth.service';
import { DataTransferService } from '@shared/services/data-transfer.service';
import { AdsApi } from '@core/apis/ads.api';
import { CookieService } from '@shared/services/cookie.service';

@Component({
	selector: 'app-navigation',
	templateUrl: './navigation.component.html',
	styleUrls: ['./navigation.component.scss'],
	providers: [DestroyService],
})
export class NavigationComponent implements OnInit {
	constructor(
		public api: ApiService,
		private router: Router,
		private matDialog: MatDialog,
		private snack: SnackService,
		public auth: AuthService,
		public locale: LocaleService,
		public viewport: ViewportService,
		public placeAd: PlaceAdService,
		private destroy: DestroyService,
		private ads: AdsService,
		private dataTransfer: DataTransferService,
		private adsApi: AdsApi,
		private activatedRoute: ActivatedRoute,
		private cookie: CookieService
	) {
		// force route reload whenever params/queries change
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
	}

	user: User = this.auth.user();
	profileUpdateSubscription: Subscription;
	name: string;
	showVipBanner = false;
	upgradeType: 'vip' | 'vipPlus';

	onLanguageChange(lang: string) {
		localStorage.setItem('lang', lang);
		this.cookie.set('lang', lang);
		this.loadTranslations(lang);
	}

	onLogout() {
		const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
			width: '250px',
			height: '120px',
			data: {
				message: this.locale.translations['user']['user-ads']['delete-ad']['n88'],
				yes: this.locale.translations['user']['user-ads']['delete-ad']['n89'],
				no: this.locale.translations['user']['user-ads']['delete-ad']['n90'],
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result === true) {
				this.auth.logout();

				this.router.navigate(['/'], {
					queryParams: {
						session: 'closed',
					},
				});

				this.snack.open(this.locale.translations['logup']['login']['s13']);
			}
		});
	}

	//convert the page to selected language
	loadTranslations(lang: string): void {
		if (this.locale.lang !== lang) {
			let currentUrl = this.router.url;
			let updatedUrl: string;

			if (currentUrl.includes(`/${this.locale.lang}/`)) {
				updatedUrl = currentUrl.replace(`/${this.locale.lang}/`, `/${lang}/`);
			} else if (currentUrl.endsWith(`/${this.locale.lang}`)) {
				updatedUrl = currentUrl.replace(`/${this.locale.lang}`, `/${lang}`);
			}

			const translationsFormData: FormData = new FormData();
			translationsFormData.set('lang', lang);

			this.api.getTranslations(translationsFormData).subscribe((res) => {
				this.locale.lang = lang;
				this.locale.translations = res['response'];
				this.setLocaleDirection(lang);

				let body = {
					lang: lang,
				};
				this.api.getProductValues(body).subscribe((res) => {
					if (res.status === '1') {
						this.ads.callsCount = res['response']['callsCount'];
						this.ads.basicProductIQD = res['response']['basicProductIQD	'];
						this.ads.vipProductIQD = res['response']['vipProductIQD'];
					}
				});

				this.router.navigateByUrl(updatedUrl);
				// this.router.navigate([], {
				// 	relativeTo: this.route,
				// 	queryParamsHandling: 'merge',
				// });
			});
		}
	}

	onLogin(): void {
		this.auth.openAuthDialog();
	}

	// TODO: Move this on APP_INITIALIZER
	private setLocaleDirection(lang: string): void {
		switch (lang) {
			case LocaleType.ARABIC:
			case LocaleType.KURDISTAN:
				this.locale.setLocaleDirection(LocaleDirection.RTL);

				break;

			case LocaleType.ENGLISH:
				this.locale.setLocaleDirection(LocaleDirection.LTR);

				break;
			default:
				this.locale.setLocaleDirection(LocaleDirection.LTR);
		}
	}

	ngOnInit(): void {
		this.adsApi.upgradeAd$.subscribe((res) => {
			if (res) {
				this.evaluateVipBannerState();
			}
		});

		this.auth.user$.pipe(takeUntil(this.destroy)).subscribe((user: User) => {
			this.user = user;
			if (this.user) {
				this.adsApi.upgradeAd.next(true);
			} else {
				this.showVipBanner = false;
				this.upgradeType = undefined;
			}
		});

		this.profileUpdateSubscription = this.dataTransfer.profileUpdate().subscribe((profile) => {
			if (profile) {
				this.user = {
					...this.user,
					name: profile.name,
				};
			}
		});

		// React to route changes to hide VIP banner on WA Alerts pages
		this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
			if (this.isWaAlertsRoute()) {
				this.showVipBanner = false;
				this.upgradeType = undefined;
			} else if (this.user) {
				this.evaluateVipBannerState();
			}
		});
	}

	private evaluateVipBannerState(): void {
		// Always hide on WA Alerts route
		if (this.isWaAlertsRoute()) {
			this.showVipBanner = false;
			this.upgradeType = undefined;
			return;
		}
		this.adsApi.getPlanTierAdListCount().subscribe((res: { freeAdCount?: number; vipAdCount?: number; vipPlusAdCount?: number }) => {
			const freeCount = res?.freeAdCount ?? 0;
			const vipCount = res?.vipAdCount ?? 0;
			const vipPlusCount = res?.vipPlusAdCount ?? 0;

			const total = freeCount + vipCount + vipPlusCount;

			// No ads at all → hide banner & reset
			if (total === 0) {
				this.showVipBanner = false;
				this.upgradeType = undefined;
				return;
			}

			if (freeCount > 0) {
				this.showVipBanner = true;
				this.upgradeType = 'vip';
			} else if (vipCount > 0) {
				this.showVipBanner = true;
				this.upgradeType = 'vipPlus';
			} else {
				this.showVipBanner = false;
				this.upgradeType = undefined;
			}
		});
	}

	isWaAlertsRoute(): boolean {
		const url = this.router.url || '';
		return url.includes('/user/wa-alerts');
	}

	onUpgrade(): void {
		if (!this.upgradeType) {
			return;
		}

		const lang = this.locale?.lang ?? 'en';
		const url = `${window.location.origin}/${lang}/user/${this.user.userID}/ads`;

		window.location.href = url;
	}
}
